import api from "../api";

type Listener = (isAccepting: boolean) => void;

const listeners = new Set<Listener>();
let currentValue: boolean | null = null;

/** Subscribe to accepting-status changes. Returns an unsubscribe function.
 *  If a value has already been observed, the subscriber is called with it
 *  immediately so newly-mounted pages sync without waiting for their own
 *  network fetch. */
export function subscribeAcceptingStatus(cb: Listener): () => void {
  listeners.add(cb);
  if (currentValue !== null) {
    try {
      cb(currentValue);
    } catch (err) {
      console.error("acceptingStatus listener threw", err);
    }
  }
  return () => {
    listeners.delete(cb);
  };
}

function notify(isAccepting: boolean) {
  currentValue = isAccepting;
  listeners.forEach((cb) => {
    try {
      cb(isAccepting);
    } catch (err) {
      console.error("acceptingStatus listener threw", err);
    }
  });
}

export function getCurrentAcceptingStatus(): boolean | null {
  return currentValue;
}

/** Clear the cache (call on logout). Without this the module-level value
 *  survives a same-tab logout→login — the SPA never reloads — so the next
 *  talent on a shared device would inherit the previous talent's Live/Offline
 *  state (seedAcceptingStatusFromFetch is a no-op while a value is cached). */
export function resetAcceptingStatus() {
  currentValue = null;
}

/**
 * Patch the user's accepting-requests flag and notify every subscribed page
 * (Header menu / Profile badge / Requests badge) so they all re-render
 * together.
 */
export async function setAcceptingStatus(
  slug: string,
  accepting: boolean
): Promise<boolean> {
  const { data } = await api.patch(`/users/${slug}/accepting-requests`, {
    isAcceptingRequests: accepting,
  });
  const newVal = data.isAcceptingRequests !== false;
  notify(newVal);
  return newVal;
}

/** Push an externally-observed status (e.g. after an initial profile fetch)
 *  to all subscribers so newly-mounted pages stay in sync. */
export function broadcastAcceptingStatus(isAccepting: boolean) {
  notify(isAccepting);
}

/** Seed the cache from a fresh fetch, but ONLY if the cache is currently
 *  empty. A toggle (via setAcceptingStatus) is always considered fresher than
 *  a fetch — fetches can return stale data due to read-replica lag, browser
 *  HTTP caching, or simply being older than the toggle. */
export function seedAcceptingStatusFromFetch(isAccepting: boolean) {
  if (currentValue === null) {
    notify(isAccepting);
  }
}
